import gql from "graphql-tag";
import {GqlTransaction} from "@/services/gql-transaction";
import TimelineService, {EntryStatus, TimelineSteps} from "@/services/timeline-service";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {random} from "lodash";
import {gqlMutationRaw, gqlQueryRaw} from "@/apollo-graphql/utils";
import {UserServiceImpl} from "@/services/user-service";
import {Vue} from "@/extensions";
import * as Sentry from "@sentry/vue";

export interface Timeline {
    id: number
    current_entry_no: number
    timeline_entries: Array<TimelineSteps>
    current_entry: any
}

export class TimelineUtil {

    private unlockMutation = gql`
        mutation unlock(
            $timeline_id: bigint!,
            $locked_by_id: bigint!,
            $locked_at: timestamptz!
        ) {
            record_lock: delete_record_lock(where: {
                table_name: {_eq: "timeline"},
                foreign_key: {_eq: $timeline_id},
                locked_by_id: {_eq: $locked_by_id},
                locked_at: {_eq: $locked_at}
            }) {
                returning {
                    id
                }
            }

        }
    `

    private checkChangesQuery = gql`
        query checkChanges(
            $timeline_id: bigint!
        ) {
            timeline: timeline_by_pk(id: $timeline_id) {
                id
                updated_at
                current_entry_no
                current_entry {
                    id
                    entry_no
                    lb_user_id
                    status
                    type
                    updated_at
                }
            }
        }
    `

    private claimRecordMutation = gql`
        mutation claimRecord(
            $timeline_id: bigint!
        ) {
            record_lock: claim_record(
                args: {tablename: "timeline", fk_id: $timeline_id},
                limit: 1
            ) {
                locked_at
                locked_by_id
                locked_by {
                    id
                    name
                }
            }
        }
    `

    private existingLockQuery = gql`
        query existingTimelineLock(
            $timeline_id: bigint!
        ) {
            record_lock(
                where: {
                    table_name: {_eq: "timeline"}
                    foreign_key: {_eq: $timeline_id}
                }
                limit: 1
            ) {
                locked_by_id
                locked_at
                locked_by {
                    id
                    name
                }
            }
        }
    `

    async lockTimelineAndExecuteAction(
        vueContext: any,
        timeline: any,
        action: () => void | Promise<unknown>,
        _error: () => void,
        finallyFn?: () => void,
    ): Promise<void> {
        let recordLock: any = null
        let lockAcquired = false
        const userId = new UserServiceImpl()?.getCurrentUser()?.jwtUser?.userId

        try {
            const claimedRecordLock = await this.claimTimelineRecord(timeline)
            if (!claimedRecordLock) {
                recordLock = await this.getExistingTimelineLock(timeline)
                throw new Error("locked")
            }

            recordLock = claimedRecordLock
            lockAcquired = true

            if (await this.wasTimelineChanged(timeline)) throw new Error("changed")

            await Promise.resolve(action())

        } catch (err: unknown) {
            let errorMsg = vueContext.$t("errors.save_bcase_error")

            if (err instanceof Error) {
                if (err.message == 'changed') errorMsg = vueContext.$t("errors.timeline_changed")
                else if (err.message == 'locked') {
                    if (recordLock?.locked_by?.name && recordLock?.locked_by_id != userId)
                        errorMsg = vueContext.$t("errors.timeline_locked", {name: recordLock?.locked_by?.name})
                    else console.error("[Timeline] Failed to execute action", err)
                } else {
                    console.error("[Timeline] Failed to execute action", err)
                }
            } else {
                console.error("[Timeline] Failed to execute action", err)
            }

            vueContext.$message({
                message: errorMsg,
                type: "error",
                duration: 10000,
                dangerouslyUseHTMLString: true
            })

            try {
                if (typeof vueContext === 'object' && vueContext) {
                    if ('formIsLoading' in vueContext) vueContext.formIsLoading = false
                    if ('transactionIsRunning' in vueContext) vueContext.transactionIsRunning = false
                    vueContext.emitter?.emit && vueContext.emitter.emit("save-finished")
                }
            } catch (_) {
            }
        } finally {
            try {
                if (lockAcquired) await this.unlock(timeline, recordLock)
            } catch (unlockError) {
                console.error("[Timeline] Failed to release record lock", unlockError)
                try {
                    Sentry.captureException(unlockError, {
                        tags: {operation: "timeline_unlock"},
                        extra: {timelineId: timeline?.id, lockedById: recordLock?.locked_by_id}
                    })
                } catch (reportError) {
                    console.error("[Timeline] Failed to report record-lock release failure", reportError)
                }
            } finally {
                if (finallyFn) finallyFn()
            }
        }
    }

    private async claimTimelineRecord(timeline: any): Promise<any> {
        const data = {timeline_id: timeline.id}
        const result = await gqlMutationRaw(this.claimRecordMutation, data)
        return result?.data?.record_lock?.[0]
    }

    private async getExistingTimelineLock(timeline: any): Promise<any> {
        const result = await gqlQueryRaw(this.existingLockQuery, {timeline_id: timeline.id})
        return result?.data?.record_lock?.[0]
    }

    private async wasTimelineChanged(timeline: any): Promise<boolean> {
        if (!timeline?.id) throw new Error("timeline_unavailable")

        const data = {timeline_id: timeline.id}
        const result = await gqlQueryRaw(this.checkChangesQuery, data)
        const dbTimeline = result?.data?.timeline
        if (!dbTimeline || dbTimeline.current_entry_no == null)
            throw new Error("timeline_unavailable")

        if (!this.hasValidCurrentEntryTimestamp(dbTimeline) || !this.hasValidCurrentEntryTimestamp(timeline))
            throw new Error("timeline_unavailable")

        if (!this.hasCurrentEntry(dbTimeline) && !this.hasCurrentEntry(timeline) &&
            (!this.hasValidTimelineTimestamp(dbTimeline) || !this.hasValidTimelineTimestamp(timeline)))
            throw new Error("timeline_unavailable")

        return !this.hasSameCurrentEntryState(dbTimeline, timeline)
    }

    private hasCurrentEntry(timeline: any): boolean {
        return !!timeline?.current_entry?.[0]
    }

    private hasValidCurrentEntryTimestamp(timeline: any): boolean {
        const currentEntry = timeline?.current_entry?.[0]
        return !currentEntry || dayjs.utc(currentEntry.updated_at).isValid()
    }

    private hasValidTimelineTimestamp(timeline: any): boolean {
        return dayjs.utc(timeline?.updated_at).isValid()
    }

    private hasSameCurrentEntryState(dbTimeline: any, localTimeline: any): boolean {
        const dbCurrentEntry = dbTimeline?.current_entry?.[0]
        const localCurrentEntry = localTimeline?.current_entry?.[0]

        if (dbTimeline?.current_entry_no != localTimeline?.current_entry_no) return false
        if (!dbCurrentEntry || !localCurrentEntry) {
            return !dbCurrentEntry && !localCurrentEntry &&
                dayjs.utc(dbTimeline.updated_at).isSame(dayjs.utc(localTimeline.updated_at))
        }

        return dbCurrentEntry.id == localCurrentEntry.id &&
            dbCurrentEntry.entry_no == localCurrentEntry.entry_no &&
            dbCurrentEntry.lb_user_id == localCurrentEntry.lb_user_id &&
            dbCurrentEntry.status == localCurrentEntry.status &&
            dbCurrentEntry.type == localCurrentEntry.type &&
            dayjs.utc(dbCurrentEntry.updated_at).isSame(dayjs.utc(localCurrentEntry.updated_at))
    }

    private async unlock(timeline: any, recordLock: any): Promise<void> {
        const data = {
            timeline_id: timeline.id,
            locked_by_id: recordLock?.locked_by_id,
            locked_at: recordLock?.locked_at
        }

        const result = await gqlMutationRaw(this.unlockMutation, data)

        if (!result?.data?.record_lock?.returning?.length) {
            throw new Error("error unlocking")
        }
    }

}

export enum ForwardType {
    open = "open",
    recognition = "recognition",
    approved = "approved",
    ordered = "ordered",
    declined = "declined",
    declinedExported = "declined-exported",
    completed = "completed",
    historicalImported = "historical-imported",
    approval = "approval",
    financialApproval = "financial-approval",
    clearing = "clearing",
    export = "export",
    accounting = "accounting",
    check = "check",
    signature = "signature",
    contractApproval = "contract-approval",
}

class TimlineActionsImpl {

    constructor() {
        dayjs.extend(utc)
        dayjs.extend(timezone)
        dayjs.tz.guess()
    }

    // ====== GQL START ======

    private FORWARD_TIMELINE_GQL = gql`
        mutation ForwardTimeline(
            $timeline_id: bigint!,
            $entry_id: Int!,
            $newStatus: String!,
            $commentEntry: String,
            $completed_at: timestamptz,
            $inc_current_entry_no: Int!
        ) {
            update_entry: update_timeline_entry(where: {
                timeline_id: {_eq: $timeline_id}
                entry_no: {_eq: $entry_id}
            }, _set: {status: $newStatus, completed_at: $completed_at, comment: $commentEntry}) {
                returning {
                    id
                }
            }

            inc_timeline: update_timeline_by_pk(pk_columns: {id: $timeline_id} _inc: {current_entry_no: $inc_current_entry_no}) {
                id
            }
        }
    `

    private ASSIGN_ENTRY_TO = gql`
        mutation AssignEntryTo(
            $timeline_id: bigint!,
            $entry_id: Int!,
            $lb_user_id: bigint!
        ) {
            assign_to: update_timeline_entry(where: {
                timeline_id: {_eq: $timeline_id}
                entry_no: {_eq: $entry_id}
            }, _set: {lb_user_id: $lb_user_id}) {
                returning {
                    id
                }
            }

        }
    `

    private UPDATE_TEXT_ENTRY_NO = gql`
        mutation UpdateTextEntry(
            $timeline_id__DAXRSW: bigint!,
            $entry_no_past: Int!,
            $newTextType_DFH20: String!
        ) {
            update_text_entry_past:update_timeline_entry(where: {
                timeline_id: {_eq: $timeline_id__DAXRSW}
                entry_no: {_eq: $entry_no_past}
            }, _set: {text_type: $newTextType_DFH20}) {
                returning {
                    id
                }
            }

        }
    `

    private FORWARD_SKIPPING_ONE_STEP = gql`
        mutation forwardSkippingOneStep(
            $timeline_id__DAXBSA: bigint!,
            $entry_no_old_current: Int!,
            $entry_no_skipped: Int!,
            $entry_no_new_current: Int!,
            $newTextType_past: String!,
            $newTextType_present: String!,
            $completedAt: timestamptz,
            $commentEntry: String
        ) {
            update_old_current_entry:update_timeline_entry(
                where: {
                    timeline_id: {_eq: $timeline_id__DAXBSA}
                    entry_no: {_eq: $entry_no_old_current}
                },
                _set: {
                    text_type: $newTextType_past,
                    status: "approved",
                    completed_at: $completedAt,
                    comment: $commentEntry
                }
            ) {
                affected_rows
            }

            update_skipped_entry:update_timeline_entry(
                where: {
                    timeline_id: {_eq: $timeline_id__DAXBSA}
                    entry_no: {_eq: $entry_no_skipped}
                },
                _set: {
                    text_type: $newTextType_past,
                    status: "approved",
                    completed_at: $completedAt,
                    lb_user_id: null
                }
            ) {
                affected_rows
            }

            update_new_current_entry:update_timeline_entry(
                where: {
                    timeline_id: {_eq: $timeline_id__DAXBSA}
                    entry_no: {_eq: $entry_no_new_current}
                },
                _set: {
                    text_type: $newTextType_present
                }
            ) {
                affected_rows
            }

            update_inc_timeline: update_timeline_by_pk(
                pk_columns: {
                    id: $timeline_id__DAXBSA
                },
                _set: {
                    current_entry_no: $entry_no_new_current
                }
            ) {
                id
            }

        }
    `
    private UPDATE_TEXT_PRESENT_ENTRY_NO = gql`
        mutation UpdateTextEntry(
            $timeline_id__DAXRSH: bigint!,
            $entry_no_present: Int!,
            $newTextType: String!
        ) {
            update_text_entry_present:update_timeline_entry(where: {
                timeline_id: {_eq: $timeline_id__DAXRSH}
                entry_no: {_eq: $entry_no_present}
            }, _set: {text_type: $newTextType}) {
                returning {
                    id
                }
            }

        }
    `

    private EXTEND_TIMELINE_GQL = gql`
        mutation ExtendTimeline(
            $entries: [timeline_entry_insert_input!]!
        ) {
            insert_timeline_entry(objects: $entries) {
                affected_rows
            }
        }
    `

    private INSERT_INTO_TIMELINE_GQL = gql`
        mutation InsertIntoTimeline(
            $timeline_id__J9vIt: bigint!,
            $currentStep__DygRb: Int!,
            $newEntryCount__5Y1OB: Int!,
            $entries__vMgIg: [timeline_entry_insert_input!]!
        ) {
            m_EQE1b: update_timeline_entry(
                where: {_and: {
                    timeline_id: {_eq: $timeline_id__J9vIt}
                    entry_no: {_gte: $currentStep__DygRb}
                }}
                _inc: {entry_no: $newEntryCount__5Y1OB}
            ) {
                affected_rows
            }

            m_uTqwR: insert_timeline_entry(objects: $entries__vMgIg) {
                affected_rows
            }

        }
    `

    private INCREMENT_TIMELINE_CURRENT_ENTRY_NO_GQL = gql`
        mutation IncTimeline(
            $timeline_to_inc_id: bigint!,
            $inc: Int!
        ) {
            m_Ixxjc: update_timeline_by_pk(pk_columns: {id: $timeline_to_inc_id} _inc: {current_entry_no: $inc}) {
                id
            }
        }
    `

    private REMOVE_NEXT_TIMELINE_ENTRIES_GQL = gql`
        mutation RemoveTimelineEntries(
            $timeline_id__DA5SD: bigint!,
            $current_entry_no: Int!
        ) {
            deleteEntry: delete_timeline_entry(where: {
                timeline_id: {_eq: $timeline_id__DA5SD}
                entry_no: {_gt: $current_entry_no}
            }){
                affected_rows
            }

        }
    `

    private REMOVE_NEXT_ENTRIES_GQL = gql`
        mutation RemoveEntries(
            $timeline_id__DA5SD: bigint!,
            $current_entry_no: Int!,
            $count_decrement_entries: Int!,
        ) {
            removeNextSteps:delete_timeline_entry(where: {
                timeline_id: {_eq: $timeline_id__DA5SD},
                entry_no: {_gt: $current_entry_no},
                type: {_in: ["approval", "send-order"]}
            }){
                affected_rows
            }

            m_EQE1x: update_timeline_entry(
                where: {_and: {
                    timeline_id: {_eq: $timeline_id__DA5SD}
                    entry_no: {_gt: $current_entry_no}
                }}
                _inc: {entry_no: $count_decrement_entries}
            ) {
                affected_rows
            }
        }
    `


    private REMOVE_ENTRY_NO_GQL = gql`
        mutation RemoveEntryNo(
            $timeline_id__DA5SH: bigint!,
            $entry_no: Int!
        ) {
            removeNextSteps:delete_timeline_entry(where: {
                timeline_id: {_eq: $timeline_id__DA5SH},
                entry_no: {_eq: $entry_no},
            }){
                affected_rows
            }

            m_EQE1Y: update_timeline_entry(
                where: {_and: {
                    timeline_id: {_eq: $timeline_id__DA5SH}
                    entry_no: {_gt: $entry_no}
                }}
                _inc: {entry_no: -1}
            ) {
                affected_rows
            }
        }
    `

    // ====== GQL END ======


    /**
     * Forward the given timeline to the next step
     */
    forwardTimeline(transaction: GqlTransaction, timeline: Timeline, forwardType: EntryStatus, incCurrentEntryNo: number = 1, comment: string = '') {
        transaction.execute(this.FORWARD_TIMELINE_GQL, {
            timeline_id: timeline.id,
            entry_id: timeline.current_entry_no,
            newStatus: forwardType,
            completed_at: dayjs().utc().format("YYYY-MM-DD HH:mm:ss"),
            inc_current_entry_no: incCurrentEntryNo,
            commentEntry: comment
        })
    }

    /**
     * Add new entries to the timline at the end of the timeline
     */
    extendTimeline(transaction: GqlTransaction, timeline: Timeline, steps: TimelineSteps) {
        transaction.execute(this.EXTEND_TIMELINE_GQL, {
            entries: steps
        })
    }

    /**
     * Add new entries to the timeline before the next step
     * - add a comment
     * - uploaded a document
     * - any other system generated, context specific message
     */
    prependTimeline(transaction: GqlTransaction, timeline: Timeline, steps: TimelineSteps, incCurrentEntryNo?: number) {
        transaction.execute(
            this.INSERT_INTO_TIMELINE_GQL,
            TimelineService.createInsertBeforeCurrent(timeline, steps)
        )
        transaction.execute(this.INCREMENT_TIMELINE_CURRENT_ENTRY_NO_GQL, {
            timeline_to_inc_id: timeline.id,
            inc: incCurrentEntryNo ?? steps.length
        })
    }

    appendTimeline(
        transaction: GqlTransaction,
        timeline: Timeline,
        steps: TimelineSteps,
        incCurrentEntryNo?: number,
        afterNo?: number
    ) {
        const timestamp = new Date().getTime() + '' + random(10)

        const variables = {
            timelineId: `timelineId_${timestamp}`,
            nextEntryNo: `nextEntryNo_${timestamp}`,
            countNewEntries: `countNewEntries_${timestamp}`,
            entries: `entries_${timestamp}`,
            inc: `inc_${timestamp}`
        }

        const mutation = `
            mutation AppendToTimeline${timestamp}(
                $${variables.timelineId}: bigint!,
                $${variables.nextEntryNo}: Int!,
                $${variables.countNewEntries}: Int!,
                $${variables.entries}: [timeline_entry_insert_input!]!,
                $${variables.inc}: Int!
            ) {
                m_EQE1b_${timestamp}: update_timeline_entry(
                    where: {_and: {
                        timeline_id: {_eq: $${variables.timelineId}}
                        entry_no: {_gte: $${variables.nextEntryNo}}
                    }}
                    _inc: {entry_no: $${variables.countNewEntries}}
                ) {
                    affected_rows
                }
    
                m_uTqwR_${timestamp}: insert_timeline_entry(objects: $${variables.entries}) {
                    affected_rows
                }
                
                m_Ixxjc_${timestamp}: update_timeline_by_pk(pk_columns: {id: $${variables.timelineId}} _inc: {current_entry_no: $${variables.inc}}) {
                    id
                }
    
            }        
        `


        const data: any = TimelineService.createInsertAfterCurrent(timeline, steps, afterNo)

        const newVariables = {
            [variables.timelineId]: data.timelineId,
            [variables.nextEntryNo]: data.nextEntryNo,
            [variables.countNewEntries]: data.countNewEntries,
            [variables.entries]: data.entries,
            [variables.inc]: incCurrentEntryNo ?? steps.length,

        }

        transaction.execute(gql(mutation), newVariables)
    }

    assignTimelineEntryTo(transaction: GqlTransaction, timeline: Timeline, userId: number) {
        transaction.execute(this.ASSIGN_ENTRY_TO, {
            timeline_id: timeline.id,
            entry_id: timeline.current_entry_no,
            lb_user_id: userId
        })
    }

    removeNextTimelineEntries(transaction: GqlTransaction, timeline: Timeline) {
        transaction.execute(this.REMOVE_NEXT_TIMELINE_ENTRIES_GQL, {
            timeline_id__DA5SD: timeline.id,
            current_entry_no: timeline.current_entry_no
        })
    }

    removeNextEntries(transaction: GqlTransaction, timeline: Timeline, countAffectedEntries: number) {
        transaction.execute(this.REMOVE_NEXT_ENTRIES_GQL, {
            timeline_id__DA5SD: timeline.id,
            current_entry_no: timeline.current_entry_no,
            count_decrement_entries: -(countAffectedEntries)
        })
    }

    removeEntryNo(transaction: GqlTransaction, timeline: Timeline, entryNo: number) {
        transaction.execute(this.REMOVE_ENTRY_NO_GQL, {
            timeline_id__DA5SH: timeline.id,
            entry_no: entryNo,
        })
    }

    removeEntriesAfterNoByType(
        transaction: GqlTransaction,
        timeline: Timeline,
        entryNo: number,
        type: string,
        countAffectedEntries: number
    ) {

        const timestamp = new Date().getTime() + '' + random(10)

        const variables = {
            timelineId: `timeline_id_${timestamp}`,
            entryNo: `entry_no_${timestamp}`,
            countDecrementEntries: `count_decrement_entries_${timestamp}`,
            stepType: `step_type_${timestamp}`,
        }

        const mutation = `mutation RemoveEntryNo${timestamp}(
                $${variables.timelineId}: bigint!,
                $${variables.entryNo}: Int!,
                $${variables.countDecrementEntries}: Int!,
                $${variables.stepType}: String!
            ) {
                removeNextStepsByType_${timestamp}:delete_timeline_entry(where: {
                    timeline_id: {_eq: $${variables.timelineId}},
                    entry_no: {_gte: $${variables.entryNo}},
                    type: {_eq: $${variables.stepType}}
                }){
                    affected_rows
                }

                m_EQE1D_${timestamp}: update_timeline_entry(
                    where: {_and: {
                        timeline_id: {_eq: $${variables.timelineId}}
                        entry_no: {_gt: $${variables.entryNo}}
                    }}
                    _inc: {entry_no: $${variables.countDecrementEntries}}
                ) {
                    affected_rows
                }
        }`

        const newVariables = {
            [variables.timelineId]: timeline.id,
            [variables.entryNo]: entryNo,
            [variables.countDecrementEntries]: -(countAffectedEntries),
            [variables.stepType]: type
        }

        transaction.execute(gql(mutation), newVariables)
    }

    changeTextCurrentEntryWithSuccessfulPast(transaction: GqlTransaction, timeline: Timeline) {
        transaction.execute(this.UPDATE_TEXT_ENTRY_NO, {
            timeline_id__DAXRSW: timeline.id,
            entry_no_past: timeline.current_entry_no,
            newTextType_DFH20: 'description_past_successful'
        })
    }

    forwardTimelineSkippingOneStep(transaction: GqlTransaction, timeline: Timeline, comment: string = '') {
        transaction.execute(this.FORWARD_SKIPPING_ONE_STEP, {
            timeline_id__DAXBSA: timeline.id,
            entry_no_old_current: timeline.current_entry_no,
            entry_no_skipped: timeline.current_entry_no + 1,
            entry_no_new_current: timeline.current_entry_no + 2,
            newTextType_past: 'description_past_successful',
            newTextType_present: 'description_present',
            completedAt: dayjs().utc().format("YYYY-MM-DD HH:mm:ss"),
            commentEntry: comment
        })
    }

    changeTextCurrentEntryWithUnsuccessfulPast(transaction: GqlTransaction, timeline: Timeline, lastStep: boolean = false) {
        transaction.execute(this.UPDATE_TEXT_ENTRY_NO, {
            timeline_id__DAXRSW: timeline.id,
            entry_no_past: timeline.current_entry_no,
            newTextType_DFH20: lastStep ? 'description_finished_unsuccessful' : 'description_past_unsuccessful'
        })
    }

    changeTextCurrentEntryToAssignmentText(transaction: GqlTransaction, timeline: Timeline) {
        transaction.execute(this.UPDATE_TEXT_ENTRY_NO, {
            timeline_id__DAXRSW: timeline.id,
            entry_no_past: timeline.current_entry_no,
            newTextType_DFH20: 'description_assignment'
        })
    }

    changeTextNextEntryWithPresent(transaction: GqlTransaction, timeline: Timeline) {
        transaction.execute(this.UPDATE_TEXT_PRESENT_ENTRY_NO, {
            timeline_id__DAXRSH: timeline.id,
            entry_no_present: timeline.current_entry_no + 1,
            newTextType: 'description_present'
        })
    }

}

const TimelineActions = new TimlineActionsImpl()
export default TimelineActions
